import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import * as crypto from 'crypto-js';
import { global } from 'src/app/global';
import { CookieService } from 'ngx-cookie-service';
import { Observable, firstValueFrom, interval, throwError, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { number } from 'echarts';
import { SseService } from './SseService';

interface OAuthOptions {
  consKey: string;
  realm: string;
  liveSessionToken?: string;
  accessToken?: string;
  requestToken?: string;
  verifier?: string;
  diffieHellmanChallenge?: string;
  url: string;
  urlReal: string;
  prepend?: string;
  useGw?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class OAuthService {

  constructor(private _sseService: SseService, private http: HttpClient, private _cookieService: CookieService) { }

  // Solo '1' enruta por OAuth firmado; '0', '2' o clave ausente van por Gateway plano.
  // La clave iboauthmethod se persiste únicamente al confirmarse un login exitoso.
  private resolveMethod(override?: string): string {
    if (override) {
      return override;
    }
    return localStorage.getItem('iboauthmethod') || '0';
  }

  apiGetIbrk(url: string, methodOverride?: string): Observable<any> {
    const usePlain = this.resolveMethod(methodOverride) !== '1';

    const options = this.getOptions(url);
      return usePlain
    ? this._sseService.apiGetNewGW(url) : this.oauthRequest(options, 'protectedResource', 'GET', {});
  }
  apiDeleteIbrk(url: string, methodOverride?: string): Observable<any> {
    const usePlain = this.resolveMethod(methodOverride) !== '1';

    const options = this.getOptions(url);
    return  usePlain ? this._sseService.apiDeleteGW(url) : this.oauthRequest(options, 'protectedResource', 'DELETE', {});
  }
  apiPostIbrk(url: string, body: any, methodOverride?: string): Observable<any> {
    const usePlain = this.resolveMethod(methodOverride) !== '1';

    const options = this.getOptions(url);
    return usePlain ? this._sseService.apiPostNewGW(url, body) : this.oauthRequest(options, 'protectedResource', 'POST', body, 'json');
  }

  // ---------------------------------------------------------------------------------------------
  // Inicio de sesion OAuth con IBKR (request_token, access_token y live_session_token).
  //
  // Las claves privadas del consumer ya no estan en el navegador: el backend firma cada paso y
  // devuelve la cabecera Authorization. La peticion a IBKR la sigue enviando el navegador, por el
  // mismo camino y con las mismas cabeceras de siempre (proxy de la aplicacion -> IBKR), asi que
  // IBKR recibe exactamente la llamada original.
  // ---------------------------------------------------------------------------------------------

  /** Envia a IBKR la peticion ya firmada por el backend. */
  private enviarFirmada(firma: { endpoint: string; authorization: string }): Promise<any> {
    const headers = new HttpHeaders().set('Authorization', firma.authorization);
    // Cuerpo '' (cadena vacia) a proposito: Angular envia content-type text/plain y content-length 0,
    // igual que la peticion firmada en el navegador de antes. Con null no enviaria content-type.
    return firstValueFrom(this.http.request('POST', global.session.baseUrlReal + firma.endpoint, { body: '', headers }));
  }

  /** oauth/request_token (flujo de terceros): devuelve el oauth_token temporal. */
  public async pedirRequestToken(consumerKey: string): Promise<string> {
    const firma = (await this._sseService.apiPost('ibkr/oauth/firmar/request-token', true, { consumerKey }, 2)).data;
    const res = await this.enviarFirmada(firma);
    return res.oauth_token;
  }

  /** oauth/access_token: devuelve oauth_token y oauth_token_secret (este ultimo cifrado con la clave publica del consumer). */
  public async pedirAccessToken(consumerKey: string, requestToken: string, verifier: string): Promise<{ oauth_token: string; oauth_token_secret: string }> {
    const firma = (await this._sseService.apiPost('ibkr/oauth/firmar/access-token', true, { consumerKey, requestToken, verifier }, 2)).data;
    return this.enviarFirmada(firma);
  }

  /**
   * live_session_token: el backend firma (descifrando el token secret y guardando el reto
   * Diffie-Hellman), el navegador envia a IBKR y el backend calcula y verifica el LST con la respuesta.
   * El token secret descifrado y el exponente Diffie-Hellman no salen del servidor.
   */
  public async generarLiveSessionToken(consumerKey: string, accessToken: string, tokenSecret: string): Promise<string> {
    const inicio = (await this._sseService.apiPost('ibkr/oauth/firmar/live-session-token', true, { consumerKey, accessToken, tokenSecret }, 2)).data;
    const res = await this.enviarFirmada(inicio);
    if (!res?.diffie_hellman_response) {
      throw new Error('IBKR no devolvió la respuesta Diffie-Hellman. Vuelve a generar el live session token.');
    }
    const fin = await this._sseService.apiPost('ibkr/oauth/completar/live-session-token', true, {
      id: inicio.id,
      dhResponse: res.diffie_hellman_response,
      lstSignature: res.live_session_token_signature
    }, 2);
    return fin.data.lst;
  }

  /** Mensaje legible de un error del backend (axios) o de IBKR (HttpErrorResponse). */
  public static mensajeError(err: any, porDefecto: string): string {
    if (err?.response?.data?.message) return err.response.data.message;
    if (err instanceof HttpErrorResponse) return 'IBKR respondió ' + err.status + '. ' + porDefecto;
    return err?.message || porDefecto;
  }

  public oauthRequest(options: OAuthOptions, requestType: string, httpMethod: string, requestBody: any, bodyType?: string): Observable<any> {
    // Solo peticiones a la cuenta: se firman con HMAC y el live session token. El inicio de sesion
    // (request_token, access_token y live_session_token) lo firma el backend en /api/ibkr/oauth/**,
    // porque la clave privada del consumer no puede estar en el navegador.
    if (requestType !== 'protectedResource') {
      return throwError(() => new Error('La firma RSA se hace en el backend: usa /api/ibkr/oauth/**'));
    }
    const signatureMethod = 'HMAC-SHA256';
    const key = this._cookieService.get('liveSessionToken');
    const oauth = this.createOAuth(options, signatureMethod);
    const oauthData = this.createOAuthData(options, requestType);
    const requestData = this.createRequestData(options.url, httpMethod, requestBody, bodyType);

    oauthData.oauth_signature = this.getSignature(requestData, key, oauthData, signatureMethod, options);
    
    const headers = this.createHeaders(oauth, oauthData, options.useGw);
    const body = this.createRequestBody(httpMethod, requestBody, bodyType, requestType);
    return this.http.request(httpMethod, options.urlReal, { body, headers });
  }
  private createOAuth(options: OAuthOptions, signatureMethod: string) {
    // hash_function eliminado: no lo usaba nadie (la firma se calcula en getSignature) y era lo
    // ultimo que referenciaba la firma RSA en el navegador.
    return {
      consumer: { key: this._cookieService.get('llave') },
      realm: options.realm,
      signature_method: signatureMethod
    };
  }

  private createOAuthData(options: OAuthOptions, requestType: string) {
    const oauthData: any = {
      oauth_consumer_key: this._cookieService.get('llave'),
      oauth_nonce: this.getNonce(),
      oauth_signature_method: this._cookieService.check('liveSessionToken') ? 'HMAC-SHA256' : 'RSA-SHA256',
      oauth_timestamp: this.getTimeStamp(),
    };
    options.accessToken = this._cookieService.get("accessToken");
    if (requestType === 'requestToken') {
      oauthData.oauth_callback = 'oob';
    } else if (requestType === 'accessToken') {
      oauthData.oauth_token = options.requestToken;
      oauthData.oauth_verifier = options.verifier;
    } else if (requestType !== 'requestToken') {
      oauthData.oauth_token = options.accessToken;
    }
    if (requestType === 'protectedResource') {
      //console.log(' accestoken '+options.accessToken);
      oauthData.oauth_token = options.accessToken;
    }
    if (options.diffieHellmanChallenge) {
      oauthData.diffie_hellman_challenge = options.diffieHellmanChallenge;
    }

    return oauthData;
  }

  private createRequestData(url: string, method: string, body: any, bodyType?: string) {
    const requestData: any = { url, method };
    if (!bodyType || bodyType === 'urlencoded') {
      requestData.data = body;
    }
    return requestData;
  }

  private getSignature(requestData: any, key: string, oauthData: any, signatureMethod: string, options: OAuthOptions): string {
    let base = this.getBaseString(requestData, oauthData);
    base = base.replace(/%257C/g, '%7C').replace(/%252C/g, '%2C').replace(/%253A/g, '%3A');
    const buffKey = crypto.enc.Base64.parse(key);
    const hmac = crypto.HmacSHA256(base, buffKey);
    return hmac.toString(crypto.enc.Base64);
  }

  private createHeaders(oauth: any, oauthData: any, useGw?: boolean): HttpHeaders {
    let headers = new HttpHeaders();

    //if (!useGw) {
    const headerValue = this.createOAuthHeaderValue(oauth, oauthData);
    headers = headers.set('Authorization', headerValue);
    //}

    return headers;
  }

  private createRequestBody(httpMethod: string, requestBody: any, bodyType?: string, requestType?: string): any {
    if (requestBody && (httpMethod === 'POST' || httpMethod === 'PUT')) {
      if (bodyType === 'json' || requestType === 'sso') {
        return JSON.stringify(requestBody);
      } else {
        return this.objectBodyToPayload(this.sortObject(requestBody));
      }
    }
    return null;
  }

  private getNonce(): string {
    return Math.random().toString(36).substring(2);
  }

  private getTimeStamp(): number {
    return Math.floor(Date.now() / 1000);
  }

  private getBaseString(requestData: any, oauthData: any): string {
    const params = { ...requestData.data, ...oauthData };
    const sortedParams = this.sortObject(params);
    const paramString = sortedParams.map(p => `${this.percentEncode(p.key)}=${this.percentEncode(p.value)}`).join('&');
    return `${requestData.method.toUpperCase()}&${this.percentEncode(requestData.url)}&${this.percentEncode(paramString)}`;
  }

  private createOAuthHeaderValue(oauth: any, oauthData: any): string {
    const sorted = this.sortObject(oauthData);
    let headerValue = 'OAuth ';

    if (oauth.realm) {
      headerValue += `realm="${oauth.realm}",`;
    }

    for (const item of sorted) {
      //if (item.key.indexOf('oauth_') === 0) {
      headerValue += `${this.percentEncode(item.key)}="${this.percentEncode(item.value)}",`;
      //}
    }


    return headerValue.slice(0, -1); // Remove trailing comma
  }

  private percentEncode(str: string): string {
    return encodeURIComponent(str)
      .replace(/!/g, '%21')
      .replace(/\*/g, '%2A')
      .replace(/'/g, '%27')
      .replace(/\(/g, '%28')
      .replace(/\)/g, '%29');
  }

  private sortObject(obj: any): Array<{ key: string, value: any }> {
    return Object.keys(obj)
      .sort()
      .map(key => ({ key, value: obj[key] }));
  }

  private objectBodyToPayload(sortedParams: Array<{ key: string, value: any }>): string {
    return sortedParams.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
  }
  getOptions(endPoint: string) {
    const options = { ...global.credentials };
    options.useGw = global.session.useGw;
    if (endPoint) {
      options.endPoint = endPoint;
      options.url = global.session.baseUrl + endPoint;
      options.urlReal = global.session.baseUrlReal + endPoint;
    }
    return options;
  }


  fetchDataEverySecoundsbrk(endpoint: string, tiempo: number): Observable<any> {
    const url = endpoint;
    return timer(0, tiempo).pipe(
      switchMap(() => this.apiGetIbrk(endpoint))
    );
  }


}